import type { TermSheet } from './types'
import { TERM_SHEET_SCHEMA_VERSION } from './types'

/**
 * Invariant checker — pure, and the mechanism that turns a derivation bug into
 * a loud failure instead of a plausible-looking wrong answer.
 *
 * Run over live fetched data in CI for every lender key and vault provider.
 * Every rule here encodes something that WOULD otherwise ship silently.
 */

export interface TermSheetViolation {
  /** Stable slug, so a test can assert on the specific rule. */
  rule: string
  message: string
  marketUid?: string
}

const approx = (a: number, b: number, tol = 1e-6) => Math.abs(a - b) <= tol

export function validateTermSheet(sheet: TermSheet): TermSheetViolation[] {
  const v: TermSheetViolation[] = []
  const uid = sheet.marketUid
  const fail = (rule: string, message: string) =>
    v.push({ rule, message, marketUid: uid })

  if (sheet.schemaVersion !== TERM_SHEET_SCHEMA_VERSION)
    fail('schema-version', `unexpected schemaVersion ${sheet.schemaVersion}`)
  if (!sheet.profileId) fail('profile-id', 'missing profileId')

  for (const side of ['supply', 'borrow'] as const) {
    const s = sheet[side]
    if (!s) continue

    // --- rate ---------------------------------------------------------------
    const c = s.rate.components
    const expected = (c.base ?? 0) + (c.rewards ?? 0) + (c.intrinsic ?? 0)
    if (!approx(s.rate.aprTotal, expected, 1e-6))
      fail(
        'apr-total',
        `${side}.rate.aprTotal ${s.rate.aprTotal} != base+rewards+intrinsic ${expected}`,
      )

    // Points are not priceable and must never inflate the headline.
    const priceable = (s.rate.rewards ?? [])
      .filter((r) => !r.indicative)
      .reduce((sum, r) => sum + r.apr, 0)
    if (s.rate.rewards && !approx(c.rewards ?? 0, priceable, 1e-6))
      fail(
        'rewards-priceable',
        `${side}.rate.components.rewards ${c.rewards} != sum of non-indicative rewards ${priceable}`,
      )

    if (s.rate.basis !== 'apr-nominal')
      fail('rate-basis', `${side}.rate.basis must be apr-nominal`)

    // --- maturity -----------------------------------------------------------
    if (s.maturity.kind === 'fixed-date' && s.maturity.maturity == null)
      fail(
        'maturity-missing',
        `${side}.maturity.kind is fixed-date with no maturity`,
      )
    if (
      (s.rate.kind === 'fixed-term' || s.rate.kind === 'fixed-open') &&
      s.rate.kind === 'fixed-term' &&
      s.maturity.kind === 'perpetual'
    )
      fail(
        'fixed-term-perpetual',
        `${side} is fixed-term but maturity.kind is perpetual`,
      )

    // --- availability -------------------------------------------------------
    if (s.availability.canOpen === false && !s.availability.blockedBy)
      fail(
        'blocked-reason',
        `${side}.availability.canOpen is false with no blockedBy`,
      )

    // --- info ---------------------------------------------------------------
    // `headline` is the graceful-degradation path for an unknown enum member,
    // so it is required rather than optional.
    if (!s.info.headline) fail('headline', `${side}.info.headline is empty`)
    if (!Array.isArray(s.info.tags))
      fail('tags', `${side}.info.tags is not an array`)
  }

  // --- supply exit ----------------------------------------------------------
  if (sheet.supply) {
    const e = sheet.supply.exit
    if (e.mode === 'instant') {
      if (e.settlement !== 'sync')
        fail('instant-sync', 'exit.mode instant must be settlement sync')
      if (e.cooldownSecs)
        fail('instant-cooldown', 'exit.mode instant must not carry a cooldown')
    }
  }

  // --- borrow liquidation ---------------------------------------------------
  if (sheet.borrow) {
    const l = sheet.borrow.liquidation
    if (l.seizure === 'full-collateral') {
      const impl = sheet.borrow.info.implications ?? []
      const tags = sheet.borrow.info.tags ?? []
      if (!tags.includes('full-collateral-seizure') && impl.length === 0)
        fail(
          'full-seizure-warning',
          'full-collateral seizure must surface a tag or an implication',
        )
    }
    if (l.penalty != null && (l.penalty < 0 || l.penalty > 1))
      fail('penalty-range', `liquidation.penalty ${l.penalty} outside 0..1`)
    if (l.closeFactor <= 0 || l.closeFactor > 1)
      fail(
        'close-factor-range',
        `liquidation.closeFactor ${l.closeFactor} outside 0..1`,
      )
  }

  // --- exposures ------------------------------------------------------------
  for (const [label, exp] of [
    ['supply.backedBy', sheet.supply?.backedBy],
    ['borrow.acceptedCollateral', sheet.borrow?.acceptedCollateral],
  ] as const) {
    if (!exp) continue
    if (exp.count !== exp.items.length && exp.items.length > 0)
      fail(
        'exposure-count',
        `${label}.count ${exp.count} != items ${exp.items.length}`,
      )

    if (exp.weightBasis === 'unweighted') {
      // The invariant that stops a fabricated pooled-lender weight shipping.
      if (exp.items.some((i) => i.weightPct != null))
        fail(
          'unweighted-has-weight',
          `${label} is unweighted but an item carries weightPct`,
        )
      if (exp.topWeightPct != null)
        fail(
          'unweighted-top-weight',
          `${label} is unweighted but has topWeightPct`,
        )
    } else if (exp.items.length > 0) {
      const sum = exp.items.reduce((s, i) => s + (i.weightPct ?? 0), 0)
      if (Math.abs(sum - 100) > 0.5)
        fail(
          'weight-sum',
          `${label} weights sum to ${sum.toFixed(2)}, expected ~100`,
        )
    }

    if (exp.items.length > 0) {
      const scores = exp.items
        .map((i) => i.quality?.riskScore)
        .filter((n): n is number => n != null)
      if (scores.length > 0 && exp.worstRiskScore != null) {
        const worst = Math.max(...scores)
        if (exp.worstRiskScore !== worst)
          fail(
            'exposure-worst-score',
            `${label}.worstRiskScore ${exp.worstRiskScore} != actual worst ${worst}`,
          )
      }
    }
  }

  // --- utilization ----------------------------------------------------------
  const u = sheet.utilization
  if (u) {
    if (u.utilization < 0 || u.utilization > 1)
      fail('utilization-range', `utilization ${u.utilization} outside 0..1`)
    if (u.basis !== 'market') {
      if (u.irmTotalDeposits == null || u.irmTotalDebt == null)
        fail(
          'irm-basis-totals',
          `utilization.basis is ${u.basis} but irmTotal* are missing`,
        )
      else if (u.irmTotalDeposits > 0) {
        const derived = u.irmTotalDebt / u.irmTotalDeposits
        if (Math.abs(derived - u.utilization) > 0.02)
          fail(
            'irm-basis-mismatch',
            `irmTotalDebt/irmTotalDeposits ${derived.toFixed(4)} != utilization ${u.utilization.toFixed(4)}`,
          )
      }
    }
  }

  // --- oracle ---------------------------------------------------------------
  const o = sheet.oracle
  if (o) {
    if (o.kind === 'none' && o.address)
      fail('oracle-none-address', 'oracle.kind none must not carry an address')
    // `oracle.kind: 'none'` means no PRICE FEED — the trigger must therefore
    // not depend on one. Two ways to satisfy that, and the second was only
    // discovered when Frankencoin arrived:
    //   - `trigger: 'time'`      — nothing is priced at all (Teller).
    //   - `model: 'auction'`     — priced by a Dutch auction against an
    //                              owner-DECLARED price (Frankencoin), so the
    //                              trigger is a price without being an oracle.
    // The original rule assumed Teller was the only feedless lender and would
    // have rejected Frankencoin as malformed.
    if (o.kind === 'none' && sheet.borrow) {
      const t = sheet.borrow.liquidation.trigger
      const m = sheet.borrow.liquidation.model
      if (t !== 'time' && t !== 'none' && m !== 'auction')
        fail(
          'no-oracle-non-time',
          `oracle.kind none but liquidation.trigger is ${t} with model ${m ?? 'unset'}`,
        )
    }
    if (o.address && o.address !== o.address.toLowerCase())
      fail('oracle-lowercase', 'oracle.address must be lowercased')
  }

  // --- governance -----------------------------------------------------------
  const g = sheet.governance
  if (g) {
    if (g.mutability === 'immutable') {
      if (g.timelockSecs)
        fail(
          'immutable-timelock',
          'immutable governance must not carry a timelock',
        )
      // `change-fees` is the one power that can coexist with an immutable
      // MARKET, and pretending otherwise would force a choice between two
      // false sheets. Morpho Blue is the case: a market's collateral, oracle,
      // IRM and LLTV have no setter, but `setFee` reaches a live market
      // (capped at 25 % of interest). Reporting `governed` would warn about
      // parameter changes that cannot happen; dropping the power would hide a
      // real claim on supplier yield. Everything else stays forbidden — a
      // market whose LLTV or oracle can be changed is not immutable.
      const disallowed = (g.powers ?? []).filter((p) => p !== 'change-fees')
      if (disallowed.length)
        fail(
          'immutable-powers',
          `immutable governance must not carry powers: ${disallowed.join(', ')}`,
        )
    }
    // A non-timelock root cannot enforce a delay; reporting one would be worse
    // than reporting nothing.
    //
    // EXCEPT when the delay is enforced by the market contract ITSELF rather
    // than inferred from a governance root — `timelockSource: 'on-chain'`. Every
    // MetaMorpho-shape vault (and its Silo / Euler Earn / TermMax cousins) reads
    // a `timelock` off the vault, and that queue is honoured no matter whether
    // the owner is a Safe, an EOA or a DAO. Demanding a TIMELOCK controller
    // there would discard a real, contract-enforced notice period on ~200 live
    // vaults because the OWNER is something else — which inverts the rule's own
    // purpose of never overstating a holder's warning window.
    if (
      g.timelockSecs &&
      g.controllerKind &&
      g.controllerKind !== 'TIMELOCK' &&
      g.timelockSource !== 'on-chain'
    )
      fail(
        'timelock-without-timelock-root',
        `timelockSecs set but controllerKind is ${g.controllerKind}`,
      )
  }

  // --- modes ----------------------------------------------------------------
  for (const side of ['supply', 'borrow'] as const) {
    const modes = sheet[side]?.modes
    if (!modes) continue
    if (modes.filter((m) => m.isDefault).length > 1)
      fail('multiple-defaults', `${side}.modes has more than one isDefault`)
  }

  return v
}

/** Validate a batch; returns every violation found, flattened. */
export function validateTermSheets(sheets: TermSheet[]): TermSheetViolation[] {
  return sheets.flatMap((s) => validateTermSheet(s))
}
